import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';

enum WifiOptions {
    WLN_SECURITY_MODE_DISABLED,
    WLN_SECURITY_MODE_WEP64,
    WLN_SECURITY_MODE_WEP128,
    WLN_SECURITY_MODE_WPA1,
    WLN_SECURITY_MODE_WPA2
}

export default class Wifi extends BaseFeature {
    constructor() {
        super();
        this.name = 'Wi-Fi';
        this.id = 'wifi';
        this.icon = 'FaWifi';
        this.priority = 99;
        this.requirements = ['tios'];
        const settings = [
            {
                id: 'ENABLED',
                name: 'Wi-Fi Enabled',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'MODE',
                name: 'Mode',
                settingType: SettingType.OPTION,
                required: true,
                // readonly: true,
                default: 'station',
                options: [
                    {
                        name: 'Station',
                        value: 'station',
                    },
                    {
                        name: 'AP',
                        value: 'ap',
                    },
                ],
            },
            {
                id: 'DHCP',
                name: 'DHCP',
                settingType: SettingType.OPTION,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFCP',
                    NAME: 'Wifi DHCP',
                    SETTINGTYPE: 'float',
                    DATATYPE: 'B',
                    MIN: 0,
                    MAX: 1,
                    DEFAULT: '1',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'Disabled',
                            value: '0',
                        },
                        {
                            name: 'Enabled',
                            value: '1',
                        },
                    ],
                },
                required: true,
                default: '1',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'IP',
                name: 'IP Address',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFIP',
                    NAME: 'Wifi IP',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '1.0.0.1',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '1.0.0.1',
            },
            {
                id: 'NETMASK',
                name: 'Netmask',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFNM',
                    NAME: 'Wifi Netmask',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '255.255.255.0',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '255.255.255.0',
            },
            {
                id: 'GATEWAY',
                name: 'Gateway',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFGW',
                    NAME: 'Wifi Gateway',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '1.0.0.1',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '1.0.0.1',
            },
            {
                id: 'WAP',
                name: 'Access Point SSID',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFAP',
                    NAME: 'Wifi AP SSID',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 32,
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: '',
            },
            {
                id: 'WSEC',
                name: 'Security Type',
                settingType: SettingType.OPTION,
                dataType: TibboDataType.SETTING,
                required: true,
                default: '7',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'WEP64',
                        value: '1',
                    },
                    {
                        name: 'WEP128',
                        value: '2',
                    },
                    {
                        name: 'WPA1-PSK (TKIP)',
                        value: '3',
                    },
                    {
                        name: 'WPA1-PSK (AES)',
                        value: '4',
                    },
                    {
                        name: 'WPA2-PSK (TKIP)',
                        value: '5',
                    },
                    {
                        name: 'WPA2-PSK (AES)',
                        value: '6',
                    },
                    {
                        name: 'AUTO',
                        value: '7',
                    },
                ],
                setting: {
                    SETTINGID: 'WSEC',
                    NAME: 'Wifi Security',
                    SETTINGTYPE: 'string',
                    MIN: 0,
                    MAX: 1,
                    DATATYPE: 'S',
                    DEFAULT: '7',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'Disabled',
                            value: '0',
                        },
                        {
                            name: 'WEP64',
                            value: '1',
                        },
                        {
                            name: 'WEP128',
                            value: '2',
                        },
                        {
                            name: 'WPA1-PSK (TKIP)',
                            value: '3',
                        },
                        {
                            name: 'WPA1-PSK (AES)',
                            value: '4',
                        },
                        {
                            name: 'WPA2-PSK (TKIP)',
                            value: '5',
                        },
                        {
                            name: 'WPA2-PSK (AES)',
                            value: '6',
                        },
                        {
                            name: 'AUTO',
                            value: '7',
                        },
                    ],
                },
            },
            {
                id: 'WPW',
                name: 'Password',
                settingType: SettingType.PASSWORD,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFPW',
                    NAME: 'Wifi Password',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 0,
                    MAX: 64,
                },
                UIControl: {
                    CONTROL: 'PASSWORD',
                },
                required: true,
                default: '',
            },
            {
                id: 'CHANNEL',
                name: 'Channel',
                settingType: SettingType.NUMBER,
                required: true,
                min: 1,
                max: 14,
                default: '6',
            },
            {
                id: 'AUTOSTART',
                name: 'Connect on Boot',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '1',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'CLK',
                name: 'Wifi CLK Line',
                settingType: SettingType.PIN,
                required: true,
                default: '14',
            },
            {
                id: 'CS',
                name: 'Wifi CS Line',
                settingType: SettingType.PIN,
                required: true,
                default: '15',
            },
            {
                id: 'DI',
                name: 'Wifi DI Line',
                settingType: SettingType.PIN,
                required: true,
                default: '12',
            },
            {
                id: 'DO',
                name: 'Wifi DO Line',
                settingType: SettingType.PIN,
                required: true,
                default: '13',
            },
            {
                id: 'RST',
                name: 'Wifi RST Line',
                settingType: SettingType.PIN,
                required: false,
                default: '11',
            },
            {
                id: 'BUF',
                name: 'Wifi Buffer',
                settingType: SettingType.BUFFER,
                required: true,
                default: '5',
            },
        ];


        this.settings = this.settings.concat(settings);
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const { project } = this;

        return errors;
    }

    getDefines(): Array<Define> {
        const defines = [
            {
                name: 'WLN_WPA',
                value: '1',
            },
            {
                name: 'WLN_AVAILABLE',
                value: '1',
            },
        ];

        return defines;
    }

    getDependencies() {
        if (['WM2000', 'WM2000EV', 'WS1102'].includes(this.project.device.id)) {
            return super.getDependencies();
        }
        const dependencies = [
            'wireless_module',
        ];
        if (this.getSettingValue('MODE') === 'station') {
            const DHCP = this.getSettingValue('DHCP');
            let DHCPIsLinked = false;
            for (let i = 0; i < this.project.features.length; i++) {
                const feature = this.project.features[i];
                if (feature.id === 'linked_setting') {
                    if (feature.getSettingValue('FEATURE') === this.getSettingValue('ID')
                        && feature.getSettingValue('SETTINGID') === 'DHCP') {
                        DHCPIsLinked = true;
                    }
                }
            }
            if (DHCP === '1' || DHCPIsLinked) {
                dependencies.push('dhcp');
            }
        }

        return dependencies;
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.DISABLED;
        }
        if (settingId === 'WPW') {
            if (this.getSettingValue('WSEC') === '0') {
                return SettingState.DISABLED;
            }
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            case 'CLK':
            case 'CS':
            case 'DI':
            case 'DO':
            case 'RST':
                return SettingState.HIDDEN;
            case 'CHANNEL':
                if (this.getSettingValue('MODE') === 'ap') {
                    return SettingState.ENABLED;
                }
                return SettingState.HIDDEN;
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        if (this.project.runtime === 'simulator') {
            return blocks;
        }

        if (this.getSettingValue('ENABLED').toString() === '0') {
            return blocks;
        }

        const { project } = this;
        let bootCode = '';
        const SSID = this.getSettingValue('WAP');
        const SECURITY = this.getSettingValue('WSEC');
        const PASSWORD = this.getSettingValue('WPW');
        const DHCP = this.getSettingValue('DHCP');
        let DHCPIsLinked = false;
        for (let i = 0; i < this.project.features.length; i++) {
            const feature = this.project.features[i];
            if (feature.id === 'linked_setting') {
                if (feature.getSettingValue('FEATURE') === this.getSettingValue('ID')
                    && feature.getSettingValue('SETTINGID') === 'DHCP') {
                    DHCPIsLinked = true;
                }
            }
        }

        if (['WM2000', 'WM2000EV', 'WS1102'].includes(project.device.id)) {
            const WPW = this.getSettingValue('WPW');
            const WAP = this.getSettingValue('WAP');
            const NETMASK = this.getSettingValue('NM');
            const IP = this.getSettingValue('IP');
            const GATEWAY = this.getSettingValue('GI');
            const DHCP = this.getSettingValue('DHCP') === '1';
            bootCode += `
    'WM2000 Wi-Fi setup
    '================================================================`;

            if (WAP !== '""' || DHCP) {
                bootCode += `
    dim reboot_required as boolean = false
        `;
            }
            if (WAP !== '""') {
                bootCode += `  
    if wln.autoconnectssid<>${WAP} then 
        wln.autoconnectssid=${WAP}
        reboot_required=true
    end if

    if wln.autoconnectpassword<>${WPW} then 
        wln.autoconnectpassword=${WPW}
        reboot_required=true
    end if
`;
            }


            if (DHCP) {
                bootCode += `   
    if wln.autodhcp<>YES then
        wln.autodhcp=YES
        
        
        reboot_required=true
    end if`;


                blocks.push({
                    location: 'on_sys_dhcp_ok',
                    blockType: BlockType.CODE,
                    template: `
        if renew=YES and wln.ip<>ip then
            'this is a lease renewal and the DHCP server has issues new IP
            'it is better to reboot than deal with the implications of the changed IP
            sys.reboot
        end if

        #if DEV_DEBUG_PRINT
            dev_debugprint("WLN DHCP OK ("+ip+")")
        #endif

        if wln.ip<>ip then
            sock.inconenabledmaster=NO
            close_interface_sockets(PL_SOCK_INTERFACE_WLN)
            wln.ip=ip
            if gateway_ip<>"" then wln.gatewayip=gateway_ip
            if netmask<>"" then wln.netmask=netmask
            sock.inconenabledmaster=YES
        end if
        interface_set(PL_SOCK_INTERFACE_WLN,YES)
        '****${this.project.getNetworkConnectedLocation('PL_SOCK_INTERFACE_WLN')}****'
            `,
                });
            } else {
                blocks.push({
                    location: 'GLOBAL_DECLARATIONS',
                    blockType: BlockType.CODE,
                    template: `
declare device_wln_ip as string
declare device_wln_mask as string
declare device_wln_gateway as string`,
                });

                blocks.push({
                    location: 'GLOBAL_DIMS',
                    blockType: BlockType.CODE,
                    template: `
dim device_wln_ip as string
dim device_wln_mask as string
dim device_wln_gateway as string`,
                });

                bootCode += `
    device_wln_ip = ${IP}
    device_wln_mask = ${NETMASK}
    device_wln_gateway  = ${GATEWAY}
    wln.ip=device_wln_ip
    wln.netmask=device_wln_mask
    wln.gatewayip=device_wln_gateway`;
            }

            if (WAP !== '""' || DHCP) {
                bootCode += `
    if reboot_required = true then
        sys.reboot
    end if
    `;
            }
        } else {
            bootCode += `
    'Wifi setup
    '================================================================
    wln_ssid=${SSID}
    wln_password=${PASSWORD}
    wln_security=${SECURITY}
    wln_dhcp_enabled=${DHCP}`;


            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
enum pl_wln_security_modes
    WLN_SECURITY_MODE_DISABLED,
    WLN_SECURITY_MODE_WEP64,
    WLN_SECURITY_MODE_WEP128,
    WLN_SECURITY_MODE_WPA1_TKIP
    WLN_SECURITY_MODE_WPA1_AES,
    WLN_SECURITY_MODE_WPA2_TKIP,
    WLN_SECURITY_MODE_WPA2_AES
end enum
        

declare wln_already_started,wln_error as no_yes
declare wln_security as pl_wln_security_modes
declare wln_security_current as pl_wln_security_modes
declare wln_ssid as string(64)
declare wln_password as string(64)
declare wln_dhcp_enabled as no_yes
declare num_wln_failures as byte

declare wln_keepalive_time as dword
declare wln_keepalive_socket_out as byte
declare wln_keepalive_scan_timeout as dword
declare sub wln_keepalive()
`,
            });

            blocks.push({
                location: 'GLOBAL_DIMS',
                blockType: BlockType.CODE,
                template: `
dim wln_already_started,wln_error as no_yes
dim wln_security as pl_wln_security_modes
dim wln_security_current as pl_wln_security_modes=0
dim wln_ssid as string(64)
dim wln_password as string(64)
dim wln_dhcp_enabled as no_yes
dim running_light as byte
dim num_wln_failures as byte

dim wln_keepalive_time as dword=0
dim wln_keepalive_socket_out as byte=255
dim wln_keepalive_scan_timeout as dword=0
`,
            });

            if (this.getSettingValue('MODE') === 'station') {
                blocks.push({
                    location: 'GLOBAL_DIMS',
                    blockType: BlockType.CODE,
                    template: 'dim wln_signal_current_tick as dword=0',
                });

                blocks.push({
                    location: 'on_sys_timer',
                    blockType: BlockType.CODE,
                    template: `
    #if SIGNAL_STRENGTH_INDICATION
        'wireless signal strength indication

        #if WLN_AVAILABLE
            if sys_timercountms>wln_signal_current_tick then
                if sys_timercountms > wln_keepalive_time then
                    wln_keepalive()
                end if
                '--------- wifi task
                if wln.task=PL_WLN_TASK_IDLE AND wln.associationstate<>PL_WLN_ASSOCIATED then
                    if wln.enabled AND wln_ssid<>"" then
                        select case wln.associationstate
                            case PL_WLN_NOT_ASSOCIATED:
                                wln_already_started=YES
                                if wln_security=7 then
                                    if wln_security_current < 3 then 
                                        'dont try WEP when in AUTO mode
                                        wln_security_current=6
                                    end if
                                else
                                    wln_security_current=wln_security
                                end if
                                select case wln_security_current
                                    case WLN_SECURITY_MODE_DISABLED:
                                        wln.setwpa(PL_WLN_WPA_DISABLED,0,"",0)
                                    case WLN_SECURITY_MODE_WEP64:
                                        wln.setwep(wln_password,PL_WLN_WEP_MODE_64)
                                    case WLN_SECURITY_MODE_WEP128:
                                        wln.setwep(wln_password,PL_WLN_WEP_MODE_128)
                                    case WLN_SECURITY_MODE_WPA1_TKIP:
                                        wln.setwpa(PL_WLN_WPA_WPA1_PSK,PL_WLN_WPA_ALGORITHM_TKIP,wln_password,PL_WLN_WPA_CAST_MULTICAST)
                                    case WLN_SECURITY_MODE_WPA1_AES:
                                        wln.setwpa(PL_WLN_WPA_WPA1_PSK,PL_WLN_WPA_ALGORITHM_AES,wln_password,PL_WLN_WPA_CAST_MULTICAST)
                                    case WLN_SECURITY_MODE_WPA2_TKIP:
                                        wln.setwpa(PL_WLN_WPA_WPA2_PSK,PL_WLN_WPA_ALGORITHM_TKIP,wln_password,PL_WLN_WPA_CAST_MULTICAST)
                                    case WLN_SECURITY_MODE_WPA2_AES:
                                        wln.setwpa(PL_WLN_WPA_WPA2_PSK,PL_WLN_WPA_ALGORITHM_AES,wln_password,PL_WLN_WPA_CAST_MULTICAST)
                                end select
                            case else:
                        end select
                    else
                        wln_already_started=NO
                    end if
                else
                    if wln_keepalive_scan_timeout<>0 AND sys_timercountms > wln_keepalive_scan_timeout then
                        wln_keepalive_scan_timeout=0
                        'took longer than 10 seconds
                        'wifi module not working even though wln.associationstate=PL_WLN_ASSOCIATED
                        if wln.associationstate=PL_WLN_ASSOCIATED then
                            sys.debugprint("WiFi> took too long to scan even though associated, resetting module")
                            interface_set(PL_SOCK_INTERFACE_WLN,NO)
                            wln_reset_module()
                        end if
                    end if
                    
                end if
                if wln.enabled=NO AND wireless_enabled=YES then
                    wln_reset_module()
                end if
                wln_signal_current_tick=sys.timercountms+500
                if wln_already_started=YES then
                    if wln_error<>NO then
                        if running_light=0 then
                            signal_strength_set(&b11111)
                            running_light=1
                        else
                            signal_strength_set(0)
                            running_light=0
                        end if
                    else
                        if interface_ready(PL_SOCK_INTERFACE_WLN)=NO then
                            signal_strength_set(val("&b1"+strgen(running_light,"0")))
                            running_light=running_light+1
                            if running_light>4 then
                                running_light=0
                            end if
                        else
                            dim x as word = (wln.rssi * 5) / 255 + 1
                            if x>5 then x=5
                            signal_strength_set("&b"+strgen(x,"1"))
                        end if

                    end if
                else
                    signal_strength_set(0)
                end if
            end if
        #endif
    #endif
            `,
                });

                blocks.push({
                    location: 'on_wln_task_complete',
                    blockType: BlockType.CODE,
                    template: `
    select case completed_task
        case PL_WLN_TASK_SETWPA,PL_WLN_TASK_SETWEP:
            if wln.associate("",wln_ssid,0,PL_WLN_BSS_MODE_INFRASTRUCTURE)<>ACCEPTED then
                sys.debugprint("WiFi> associate failed")
            else
                sys.debugprint("WiFi> associating to "+wln_ssid + " with security " + str(wln_security_current))
            end if
        case PL_WLN_TASK_ASSOCIATE:
            if wln.associationstate=PL_WLN_ASSOCIATED then
                sys.debugprint("WiFi> associated to "+wln_ssid)
                if wln_dhcp_enabled=NO then
                    wln.ip=${this.getSettingValue('IP')}
                    wln.netmask=${this.getSettingValue('NETMASK')}
                    wln.gatewayip=${this.getSettingValue('GATEWAY')}
                    interface_set(PL_SOCK_INTERFACE_WLN,YES)
                end if
            else
                if wln_security=7 then
                    wln_security_current=wln_security_current-1
                end if
                sys.debugprint("WiFi> associate failed")
            end if
        case PL_WLN_TASK_ACTIVESCAN:
            wln_keepalive_scan_timeout=0
        case else:
    end select
            `,
                });

                blocks.push({
                    location: 'on_wln_event',
                    blockType: BlockType.CODE,
                    template: `
    if wln_event=PL_WLN_EVENT_DISASSOCIATED OR wln_event=PL_WLN_EVENT_DISABLED then
        sys.debugprint("WiFi> disconnected")
        interface_set(PL_SOCK_INTERFACE_WLN,NO)
        if wln_event=PL_WLN_EVENT_DISABLED OR wln.task<>PL_WLN_TASK_IDLE then
            wln_reset_module()
        end if
        '****callback_wifi_disconnected****'
    end if
    `,
                });

                blocks.push({
                    location: 'callback_wln_failure',
                    blockType: BlockType.CODE,
                    template: `
    'interface_set(PL_SOCK_INTERFACE_WLN,NO)`,
                });

                bootCode += `
    dim wln_use_dhcp as string(1) = ${DHCP}

    if wln_use_dhcp = "1" then
        wln_dhcp_enabled = YES
    else
        wln_dhcp_enabled = NO
    end if`;

                if (DHCP === '1' || DHCPIsLinked) {
                    bootCode += `
    'wifi DHCP    
    if wln_dhcp_enabled = YES then
        dhcp_start(PL_SOCK_INTERFACE_WLN,${this.getSettingValue('IP')},"")
    end if`;

                    // write back to setting if linked setting exists
                    let ipSettingCode = '';
                    const ipSetting = project.getLinkedSetting(this.getID(), 'IP');
                    if (ipSetting !== undefined) {
                        const settingItem = project.getFeature(ipSetting.getSettingValue('LINKEDSETTING'));
                        ipSettingCode += `
    var_${settingItem.getSettingValue('SETTINGID')}_set(wln.ip)`;
                    }
                    const gatewaySetting = project.getLinkedSetting(this.getID(), 'GATEWAY');
                    if (gatewaySetting !== undefined) {
                        const settingItem = project.getFeature(gatewaySetting.getSettingValue('LINKEDSETTING'));
                        ipSettingCode += `
    var_${settingItem.getSettingValue('SETTINGID')}_set(wln.gatewayip)`;
                    }
                    const netmaskSetting = project.getLinkedSetting(this.getID(), 'NETMASK');
                    if (netmaskSetting !== undefined) {
                        const settingItem = project.getFeature(netmaskSetting.getSettingValue('LINKEDSETTING'));
                        ipSettingCode += `
    var_${settingItem.getSettingValue('SETTINGID')}_set(wln.netmask)`;
                    }

                    blocks.push({
                        location: 'callback_dhcp_ok',
                        blockType: BlockType.CODE,
                        template: `
    if interface=PL_SOCK_INTERFACE_WLN then
        if renew=YES and wln.ip<>ip then
            'this is a lease renewal and the DHCP server has issues new IP
            'it is better to reboot than deal with the implications of the changed IP
            sys.reboot
        end if
        
        #if DEV_DEBUG_PRINT
            dev_debugprint("Wi-Fi DHCP OK ("+ip+")")
        #endif
        
        if wln.ip<>ip then
            sock.inconenabledmaster=NO
            close_interface_sockets(PL_SOCK_INTERFACE_WLN)
            wln.ip=ip
            if gateway_ip<>"" then wln.gatewayip=gateway_ip
            if netmask<>"" then wln.netmask=netmask
            sock.inconenabledmaster=YES
        end if
        interface_set(PL_SOCK_INTERFACE_WLN,YES)
        ${ipSettingCode}
    end if
                `,
                    });
                } else {
                    blocks.push({
                        location: 'callback_wln_ok',
                        blockType: BlockType.CODE,
                        template: `
        interface_set(PL_SOCK_INTERFACE_WLN,YES)`,
                    });
                }
            } else if (this.getSettingValue('MODE') === 'ap') {
                bootCode += `
    wln.buffrq(${this.getSettingValue('BUF')})
    sys.buffalloc()
    wln.setwep("",PL_WLN_WEP_MODE_DISABLED)
    while wln.task<>PL_WLN_TASK_IDLE
    wend`;
                switch (SECURITY) {
                    case 'WLN_SECURITY_MODE_WEP64':
                        bootCode += `
    wln.setwep(${PASSWORD},PL_WLN_WEP_MODE_64)`;
                        break;
                    case 'WLN_SECURITY_MODE_WEP128':
                        bootCode += `
    wln.setwep(${PASSWORD},PL_WLN_WEP_MODE_128)`;
                        break;
                    case 'WLN_SECURITY_MODE_WPA1':
                        bootCode += `
    wln.setwpa(PL_WLN_WPA_WPA1_PSK,PL_WLN_WPA_ALGORITHM_TKIP,${PASSWORD},PL_WLN_WPA_CAST_MULTICAST)`;
                        break;
                    case 'WLN_SECURITY_MODE_WPA2':
                        bootCode += `
    wln.setwpa(PL_WLN_WPA_WPA2_PSK,PL_WLN_WPA_ALGORITHM_AES,${PASSWORD},PL_WLN_WPA_CAST_MULTICAST)`;
                        break;
                    default:

                        break;
                }
                bootCode += `
    wln.networkstart(${this.getSettingValue('WAP')},${this.getSettingValue('CHANNEL')})
    while wln.associationstate <> PL_WLN_OWN_NETWORK
    wend`;
            }

            bootCode += `
    '-----------------------------------------------------------------`;

            if (this.getSettingValue('MODE') === 'station') {
                bootCode += `
    wln_error=NO
    wln_already_started=YES
    num_wln_failures=0
    `;
            }

            blocks.push({
                location: 'interface_set',
                blockType: BlockType.CODE,
                template: `
    if interface=PL_SOCK_INTERFACE_WLN AND state=YES then
        wln_keepalive()
    end if
        `,
            });

            blocks.push({
                location: 'DEVICE_TBS',
                blockType: BlockType.CODE,
                template: `
sub wln_keepalive()
    if interface_ready(PL_SOCK_INTERFACE_WLN)<>YES then
        exit sub
    end if
    wln_keepalive_time=sys.timercountms + 1000 * 30 * 1

    if wln_keepalive_socket_out=255 then
        wln_keepalive_socket_out=sock_get("KA")
        sock.num=wln_keepalive_socket_out
        
        sock.targetip = wln.gatewayip
        sock.targetport=65535
        sock.protocol=PL_SOCK_PROTOCOL_TCP
        sock.targetbcast=YES
        sock.targetinterface=PL_SOCK_INTERFACE_WLN
        sock.rxbuffrq(1)
        sock.txbuffrq(1)
        sys.buffalloc()
    end if

    if wln.associationstate=PL_WLN_ASSOCIATED then
        'wln.activescan(wln_ssid)
        wln_keepalive_scan_timeout=sys.timercountms + 1000 * 10

        sock.num=wln_keepalive_socket_out
        sock.setdata("1")
        sock.send()
        sock.connect()
    end if
    
end sub
            `,
            });

            blocks.push({
                location: project.getNetworkConnectedLocation(),
                blockType: BlockType.CODE,
                template: `
if interface_ready(PL_SOCK_INTERFACE_WLN)=YES then
    wln_keepalive()
end if`,
            });
        }

        blocks.push({
            location: 'on_sock_event',
            blockType: BlockType.CODE,
            template: `
if sock.num=wln_keepalive_socket_out then
    if newstatesimple=PL_SSTS_AO then
        wln_keepalive_scan_timeout = 0
        'sys.debugprint("WiFi> keepalive socket open")
        sock.close()
    end if
end if
            `,
        });

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: bootCode,
        });

        return blocks;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/object_wln';
    }
}
